import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './EnquiryPage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';

const STATIC_PRODUCTS = [
  { _id: 'prod_rotavator_6ft', name: 'Rotavator Standard 6FT', slug: 'rotavator-standard-6ft' },
  { _id: 'prod_plough_3b', name: 'MB Plough 3 Bottom', slug: 'mb-plough-3-bottom' },
  { _id: 'prod_harrow_20d', name: 'Disc Harrow 20 Disc', slug: 'disc-harrow-20-disc' },
  { _id: 'prod_rotavator_7ft', name: 'Rotavator Premium 7FT', slug: 'rotavator-premium-7ft' },
  { _id: 'prod_plough_hd', name: 'MB Plough Heavy Duty', slug: 'mb-plough-heavy-duty' },
  { _id: 'prod_harrow_22d', name: 'Disc Harrow 22 Disc', slug: 'disc-harrow-22-disc' }
];

const EnquiryPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const urlProductSlug = searchParams.get('product');
  const urlProductName = searchParams.get('productName');
  const urlInterest = searchParams.get('interest');

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const dropdownRef = useRef(null);

  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    productId: '',
    productName: '',
    state: 'Bihar',
    district: '',
    message: ''
  });

  const envApiUrl = process.env.REACT_APP_API_URL?.trim();
  const enableLocalApi = process.env.REACT_APP_ENABLE_LOCAL_API === 'true';
  const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.');
  const API_URL = envApiUrl || '/api';
  const useStaticCatalogOnly = process.env.NODE_ENV === 'development'
    && isLocalHost
    && !!envApiUrl
    && envApiUrl.includes('localhost')
    && !enableLocalApi;

  const fetchConfig = useMemo(() => ({
    apiUrl: API_URL,
    useStaticCatalogOnly
  }), [API_URL, useStaticCatalogOnly]);

  // Load products + categories list for dropdown
  useEffect(() => {
    const fetchData = async () => {
      if (fetchConfig.useStaticCatalogOnly) {
        setProducts(STATIC_PRODUCTS);
        setLoadingProducts(false);
        return;
      }

      try {
        const ts = new Date().getTime();
        const [prodRes, catRes] = await Promise.all([
          fetch(`${fetchConfig.apiUrl}/products?limit=500&_t=${ts}`),
          fetch(`${fetchConfig.apiUrl}/categories?limit=100&_t=${ts}`)
        ]);
        const prodJson = await prodRes.json();
        const catJson = await catRes.json();

        if (prodRes.ok && prodJson.success && Array.isArray(prodJson.data) && prodJson.data.length > 0) {
          setProducts(prodJson.data);
        } else {
          setProducts(STATIC_PRODUCTS);
        }
        if (catRes.ok && catJson.success && Array.isArray(catJson.data)) {
          setCategories(catJson.data);
        }
      } catch (err) {
        console.error('Failed to load data for enquiry form:', err);
        setProducts(STATIC_PRODUCTS);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchData();
  }, [fetchConfig]);

  // Pre-fill fields if product information is passed in query parameters
  useEffect(() => {
    if (urlInterest) {
      setForm(prev => ({
        ...prev,
        productId: 'other',
        productName: urlInterest,
        message: urlInterest === 'Subsidy Apply' 
          ? 'I want to apply for a government subsidy on agricultural implements. Please guide me through the document requirements and process.'
          : urlInterest === 'Offer Plan'
          ? 'I am interested in learning about the latest offer plans, combo deals, and special crop-season discounts on farm machinery.'
          : urlInterest === 'Machinery Loan'
          ? 'I want to enquire about tractor and farm machinery loan options, interest rates, and flexible EMIs.'
          : prev.message
      }));
      return;
    }

    if (!loadingProducts && products.length > 0) {
      let matchingProduct = null;

      if (urlProductSlug) {
        matchingProduct = products.find(
          p => p.slug === urlProductSlug || p._id === urlProductSlug
        );
      }

      if (matchingProduct) {
        setForm(prev => ({
          ...prev,
          productId: matchingProduct._id,
          productName: matchingProduct.name
        }));
      } else if (urlProductName) {
        // Fallback to name match or just custom product name
        const matchByName = products.find(
          p => p.name.toLowerCase() === urlProductName.toLowerCase()
        );
        setForm(prev => ({
          ...prev,
          productId: matchByName ? matchByName._id : 'custom',
          productName: urlProductName
        }));
      } else {
        // Default to first product if none specified
        setForm(prev => ({
          ...prev,
          productId: products[0]._id,
          productName: products[0].name
        }));
      }
    }
  }, [loadingProducts, products, urlProductSlug, urlProductName, urlInterest]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProductDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredProductsForDropdown = useMemo(() => {
    let filtered = [...products];
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => {
        const catId = typeof p.category === 'object' ? p.category?._id : p.category;
        return catId === selectedCategory;
      });
    }
    // Add "Other" option only when showing all or after filtering
    const allOptions = [...filtered, { _id: 'other', name: t('enquiry_page.other_enquiry', 'Other / General Enquiry') }];
    if (!productSearch) return allOptions;
    return allOptions.filter(p => p.name.toLowerCase().includes(productSearch.toLowerCase()));
  }, [products, productSearch, selectedCategory, t]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'productId') {
      if (value === 'other') {
        setForm(prev => ({
          ...prev,
          productId: 'other',
          productName: 'General / Other Equipment'
        }));
      } else {
        const selected = products.find(p => p._id === value);
        setForm(prev => ({
          ...prev,
          productId: value,
          productName: selected ? selected.name : ''
        }));
      }
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);

    // Prepare message with details
    const fullMessage = `Product of Interest: ${form.productName}\nState: ${form.state}\nDistrict: ${form.district || 'Not Specified'}\n\nMessage: ${form.message || 'I am interested in this agricultural equipment and want to know details about pricing and delivery.'}`;

    // Payload matching Lead model requirements
    const payload = {
      name: form.name,
      phone: form.phone,
      type: 'ProductEnquiry',
      // Send valid mongoose ID if matches database product, otherwise null
      product: (form.productId && form.productId !== 'other' && form.productId !== 'custom' && form.productId.startsWith('prod_') === false) ? form.productId : null,
      message: fullMessage,
      source: 'website-enquiry'
    };

    if (form.email && form.email.trim() !== '') {
      payload.email = form.email;
    }

    try {
      const response = await fetch(`${fetchConfig.apiUrl}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const json = await response.json();

      if (response.ok && json.success) {
        alert(t('enquiry_page.success_msg'));
        setForm(prev => ({
          ...prev,
          name: '',
          phone: '',
          email: '',
          district: '',
          message: ''
        }));
      } else {
        alert(json.message || t('enquiry_page.fail_msg'));
      }
    } catch (err) {
      console.error('Error submitting enquiry:', err);
      alert(t('enquiry_page.error_msg'));
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="enquiry-page">
      <Header />
      <div className="enquiry-top-bar"></div>

      <section className="enquiry-hero">
        <div className="container" style={{ position: 'relative' }}>
          <button className="enquiry-back-btn" onClick={() => navigate(-1)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            {t('enquiry_page.back', 'Back')}
          </button>
          <h1 className="enquiry-title">
            {urlInterest === 'Subsidy Apply' ? 'Apply for Subsidy' :
             urlInterest === 'Offer Plan' ? 'Register for Special Offers' :
             urlInterest === 'Machinery Loan' ? 'Apply for Machinery Loan' :
             t('enquiry_page.title')}
          </h1>
          <p className="enquiry-subtitle">
            {urlInterest === 'Subsidy Apply' ? 'Submit your details to receive support for government agricultural subsidies' :
             urlInterest === 'Offer Plan' ? 'Get detailed information about crop-season discount packages and farmer schemes' :
             urlInterest === 'Machinery Loan' ? 'Request easy machinery financing options, minimum interest rates and EMI details' :
             t('enquiry_page.subtitle')}
          </p>
        </div>
      </section>

      <section className="enquiry-main">
        <div className="container">
          <div className="enquiry-grid">
            {/* Information Card (Left Side) */}
            <div className="enquiry-info-card">
              <h2 className="card-heading">{t('enquiry_page.why_buy')}</h2>
              
              <div className="info-row">
                <div className="info-icon green">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <div>
                  <strong>{t('enquiry_page.genuine_title')}</strong>
                  <p>{t('enquiry_page.genuine_desc')}</p>
                </div>
              </div>

              <div className="info-row">
                <div className="info-icon orange">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                </div>
                <div>
                  <strong>{t('enquiry_page.subsidy_title')}</strong>
                  <p>{t('enquiry_page.subsidy_desc')}</p>
                </div>
              </div>

              <div className="info-row">
                <div className="info-icon green">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="3" width="15" height="13"></rect>
                    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                    <circle cx="5.5" cy="18.5" r="2.5"></circle>
                    <circle cx="18.5" cy="18.5" r="2.5"></circle>
                  </svg>
                </div>
                <div>
                  <strong>{t('enquiry_page.delivery_title')}</strong>
                  <p>{t('enquiry_page.delivery_desc')}</p>
                </div>
              </div>

              <div className="info-row">
                <div className="info-icon orange">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l2.27-2.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                </div>
                <div>
                  <strong>{t('enquiry_page.support_title')}</strong>
                  <p dangerouslySetInnerHTML={{ __html: t('enquiry_page.support_desc') }}></p>
                </div>
              </div>
            </div>

            {/* Form Card (Right Side) */}
            <div className="enquiry-form-card">
              <h2 className="card-heading">{t('enquiry_page.form_title')}</h2>
              <form onSubmit={handleSubmit} className="enquiry-form">
                
                <label className="form-label">{t('enquiry_page.name_label')} <span className="req">*</span></label>
                <input
                  type="text"
                  name="name"
                  placeholder={t('enquiry_page.name_ph')}
                  value={form.name}
                  onChange={handleChange}
                  required
                  className="form-input"
                />

                <label className="form-label">{t('enquiry_page.phone_label')} <span className="req">*</span></label>
                <input
                  type="tel"
                  name="phone"
                  placeholder={t('enquiry_page.phone_ph')}
                  value={form.phone}
                  onChange={handleChange}
                  required
                  className="form-input"
                />

                <label className="form-label">{t('enquiry_page.email_label')}</label>
                <input
                  type="email"
                  name="email"
                  placeholder={t('enquiry_page.email_ph')}
                  value={form.email}
                  onChange={handleChange}
                  className="form-input"
                />

                <label className="form-label">{t('enquiry_page.product_label')} <span className="req">*</span></label>
                {loadingProducts ? (
                  <div className="loading-dropdown">{t('enquiry_page.loading_products')}</div>
                ) : (
                  <div className="searchable-dropdown-container" ref={dropdownRef}>
                    <div 
                      className="searchable-dropdown-selected form-input" 
                      onClick={() => setIsProductDropdownOpen(!isProductDropdownOpen)}
                    >
                      <span>{form.productName || t('enquiry_page.select_product', 'Select a product')}</span>
                      <svg className={`dropdown-arrow ${isProductDropdownOpen ? 'open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                    </div>
                    
                    {isProductDropdownOpen && (
                      <div className="searchable-dropdown-menu">
                        {/* Category Filter Tabs */}
                        {categories.length > 0 && (
                          <div className="dropdown-category-tabs">
                            <button
                              type="button"
                              className={`dropdown-cat-tab ${selectedCategory === 'all' ? 'active' : ''}`}
                              onClick={(e) => { e.stopPropagation(); setSelectedCategory('all'); setProductSearch(''); }}
                            >
                              All
                            </button>
                            {categories.map(cat => (
                              <button
                                type="button"
                                key={cat._id}
                                className={`dropdown-cat-tab ${selectedCategory === cat._id ? 'active' : ''}`}
                                onClick={(e) => { e.stopPropagation(); setSelectedCategory(cat._id); setProductSearch(''); }}
                              >
                                {cat.name}
                              </button>
                            ))}
                          </div>
                        )}
                        {/* Search Input */}
                        <div className="searchable-dropdown-search">
                          <input 
                            type="text" 
                            placeholder={t('enquiry_page.search_products', 'Search products...')}
                            value={productSearch}
                            onChange={(e) => setProductSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                          />
                        </div>
                        {/* Product List */}
                        <div className="searchable-dropdown-list">
                          {filteredProductsForDropdown.length > 0 ? (
                            filteredProductsForDropdown.map(p => (
                              <div 
                                key={p._id} 
                                className={`searchable-dropdown-item ${form.productId === p._id ? 'active' : ''}`}
                                onClick={() => {
                                  setForm(prev => ({ ...prev, productId: p._id, productName: p.name }));
                                  setIsProductDropdownOpen(false);
                                  setProductSearch('');
                                }}
                              >
                                {p.name}
                              </div>
                            ))
                          ) : (
                            <div className="searchable-dropdown-empty">{t('enquiry_page.no_products_found', 'No products found')}</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="form-row-double">
                  <div>
                    <label className="form-label">{t('enquiry_page.state_label')}</label>
                    <input
                      type="text"
                      name="state"
                      value={form.state}
                      disabled
                      className="form-input disabled"
                    />
                  </div>
                  <div>
                    <label className="form-label">{t('enquiry_page.district_label')} <span className="req">*</span></label>
                    <input
                      type="text"
                      name="district"
                      placeholder={t('enquiry_page.district_ph')}
                      value={form.district}
                      onChange={handleChange}
                      required
                      className="form-input"
                    />
                  </div>
                </div>

                <label className="form-label">{t('enquiry_page.req_label')}</label>
                <textarea
                  name="message"
                  placeholder={t('enquiry_page.req_ph')}
                  value={form.message}
                  onChange={handleChange}
                  rows={4}
                  className="form-textarea"
                ></textarea>

                <button type="submit" className="btn-submit-enquiry" disabled={submitLoading}>
                  {submitLoading ? (
                    <span className="btn-spinner-text">
                      <span className="spinner"></span> {t('enquiry_page.btn_submitting')}
                    </span>
                  ) : (
                    t('enquiry_page.btn_submit')
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default EnquiryPage;


